import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import microSimService from '../services/microSimService';
import tokenStorage from '../utils/tokenStorage';

/**
 * Moves a participant onto their micro simulation the moment they are paired.
 *
 * Two signals, covering the two ways someone arrives:
 *   - `msim:assigned` — pushed to this participant's VERIFIED room when the trainer
 *     launches or pairs late joiners. Only a socket holding this participant's signed
 *     token is in that room, so no filtering by id is needed (or possible to spoof).
 *   - on mount, GET .../mine — the reconnect / late-load path: someone whose page loads
 *     after they were paired never saw the push.
 *
 * Without a participant token for this session there is nothing to look up: micro
 * simulations need verified identity (Phase 0), so the hook stays quiet. *
 * USES THE PAGE'S SOCKET — pass in the host page's own useWebSocket() result; never
 * call useWebSocket here. useWebSocket wires its connect/disconnect state to the FIRST
 * handle a page opens; any later handle reads `connected` once at mount and never
 * updates. A hook opening its own handle before the page's left the page's banner
 * stuck on "Reconnecting… / Offline / Live updates are not reaching this device"
 * after every refresh, while live updates were in fact arriving.
 *
 * @param socket     the host page's useWebSocket(sessionId) result
 * @param sessionId  the session the page is showing
 */
const useMicroSimAutoJoin = (socket, sessionId) => {
  const navigate = useNavigate();
  const on = socket?.on;
  const off = socket?.off;

  useEffect(() => {
    if (!sessionId || !on || !off || !tokenStorage.getParticipantToken(sessionId)) return undefined;
    let alive = true;

    const go = (runId) => {
      if (alive && runId) navigate(`/session/${sessionId}/micro-sim/${runId}`);
    };

    microSimService.mine(sessionId)
      .then((res) => go(res?.active?.runId))
      .catch(() => { /* no micro sim running, or no access — stay on this page */ });

    const onAssigned = (payload) => {
      if (String(payload?.sessionId) === String(sessionId)) go(payload.runId);
    };
    on('msim:assigned', onAssigned);
    return () => {
      alive = false;
      off('msim:assigned', onAssigned);
    };
  }, [sessionId, on, off, navigate]);
};

export default useMicroSimAutoJoin;
